/**
 * Create the Renewal Confirm Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail, vehicleSelectedGuardrail, userDataRequiredGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createRenewalConfirmAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'renewal_confirm_agent',
    name: 'Renewal Confirm Agent',
    description: 'Step 3: Confirm data and submit renewal',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger the AI to show confirmation screen
    initialPrompt: 'The vehicle has been selected. Show the user a summary of their information (name, mobile from context) and the selected vehicle. Tell them to click the "Confirm & Submit" button if everything looks correct.',
    
    // Must have vehicle selected and user data
    entryGuardrails: {
      required: [languageDetectedGuardrail, vehicleSelectedGuardrail, userDataRequiredGuardrail],
      optional: [],
    },
    
    exitGuardrails: {
      transitions: new Map([
        ['submission_complete', {
          target: { agentId: 'renewal_result_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'confirm_renewal_data') {
        const confirmed = args.confirmed;
        console.log('[RenewalConfirmAgent] User confirmed:', confirmed);
        
        // Just acknowledge - UI handles submission
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: confirmed ? 'User confirmed, waiting for submission' : 'User wants to modify',
          },
        };
      }
      
      if (toolName === 'submission_complete') {
        const success = args.success;
        const requestId = args.request_id;
        const errorMessage = args.error_message;
        
        console.log('[RenewalConfirmAgent] Submission complete:', { success, requestId, errorMessage });
        
        return {
          toolName,
          args,
          result: { 
            success, 
            requestId,
            errorMessage,
          },
          triggersTransition: true,
          transitionTo: 'renewal_result_agent',
          transitionData: {
            renewalResult: {
              success,
              RequestId: requestId,
              Message: errorMessage,
            },
          },
        };
      }
      
      if (toolName === 'cancel_renewal') {
        console.log('[RenewalConfirmAgent] User cancelled');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            reportData: undefined,
            selectedVehicle: undefined,
            renewalResult: undefined,
          },
        };
      }
      
      // Inline FAQ - answer questions without leaving renewal flow
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[RenewalConfirmAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then remind user to confirm their data.',
          },
          triggersTransition: false,
        };
      }
      
      // Universal navigation: Switch to lost report search
      if (toolName === 'start_lost_report_search') {
        console.log('[RenewalConfirmAgent] Switching to lost report search');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to lost report search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
        };
      }
      
      // Universal navigation: Switch to accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[RenewalConfirmAgent] Switching to accident reporting');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
        };
      }
      
      // Universal navigation: Switch to FAQ
      if (toolName === 'go_to_faq') {
        console.log('[RenewalConfirmAgent] Switching to FAQ');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      const userData = context.transitionData.userData;
      const vehicle = context.transitionData.selectedVehicle;
      console.log('[RenewalConfirmAgent] Activated');
      console.log('  User:', userData?.username);
      console.log('  Vehicle:', vehicle?.Plate?.Number);
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[RenewalConfirmAgent] Deactivated');
    },
  };
}

export default createRenewalConfirmAgent;

